import type { SeriesRef } from '../dicomweb';
import type { ViewerShell } from '../shell';
import { PLAYABLE_VIDEO_SYNTAXES } from './kind';
import { downloadBytes, encapsulatedPixelData, fetchBytes, retrieveInstance } from './retrieve';

/**
 * Encapsulated video (spec section 1.2): the WADO-RS `rendered` MP4 is fetched with the viewer ticket
 * and played in a <video> with controls. PACS that do not render video (Orthanc) are served by
 * retrieving the instance and unwrapping the MPEG-4 stream from its Pixel Data. Anything a browser
 * cannot play (MPEG-2, HEVC, a stream that fails to decode) gets the IMEXHS fallback text and a download.
 */

export const VIDEO_TEXT = {
  unknownFormat: ' No se reconoce el formato de vídeo. ',
  cannotPlay: ' No es posible reproducir el vídeo en el visor. ',
  mustDownload: ' Para poder reproducirlo, debe de descargarlo. ',
  download: 'Descargar el video',
  playerTitle: 'Video',
} as const;

const MP4 = 'video/mp4';
const MPEG2 = 'video/mpeg';
const MPEG2_SYNTAXES = new Set(['1.2.840.10008.1.2.4.100', '1.2.840.10008.1.2.4.101']);

interface VideoStream {
  bytes: Uint8Array;
  playable: boolean;
  mime: string;
}

async function fetchRendered(shell: ViewerShell, series: SeriesRef): Promise<VideoStream | undefined> {
  try {
    const fetched = await fetchBytes(shell, `${shell.ctx.client.instanceUrl(series, series.instances[0])}/rendered`, MP4);
    if (!fetched.contentType.toLowerCase().startsWith('video/') || fetched.bytes.length === 0) return undefined;
    return { bytes: fetched.bytes, playable: fetched.contentType.toLowerCase().startsWith(MP4), mime: MP4 };
  } catch {
    // Not every PACS renders video; the instance retrieve below covers them.
    return undefined;
  }
}

/** The stream to play: rendered MP4 when the PACS offers it, else unwrapped from the instance's Pixel Data. */
export async function fetchVideo(shell: ViewerShell, series: SeriesRef): Promise<VideoStream> {
  const rendered = await fetchRendered(shell, series);
  if (rendered) return rendered;
  const dataSet = await retrieveInstance(shell, series, series.instances[0]);
  const syntax = (dataSet.string('x00020010') ?? '').replace(/\0/g, '').trim();
  const bytes = encapsulatedPixelData(dataSet);
  if (!bytes) throw new Error('video: no encapsulated Pixel Data');
  const playable = PLAYABLE_VIDEO_SYNTAXES.has(syntax);
  return { bytes, playable, mime: MPEG2_SYNTAXES.has(syntax) ? MPEG2 : MP4 };
}

function extensionFor(mime: string): string {
  return mime === MPEG2 ? 'mpg' : 'mp4';
}

/** " No se reconoce el formato de vídeo. " + the two explanation lines + "Descargar el video". */
function showUnsupported(host: HTMLElement, stream: VideoStream, filename: string): void {
  host.replaceChildren();
  const box = document.createElement('div');
  box.className = 'video-unsupported';
  box.setAttribute('role', 'status');
  for (const text of [VIDEO_TEXT.unknownFormat, VIDEO_TEXT.cannotPlay, VIDEO_TEXT.mustDownload]) {
    const line = document.createElement('p');
    line.textContent = text;
    box.appendChild(line);
  }
  const download = document.createElement('button');
  download.type = 'button';
  download.className = 'display-button video-download';
  download.textContent = VIDEO_TEXT.download;
  download.addEventListener('click', () => downloadBytes(stream.bytes, stream.mime, `${filename}.${extensionFor(stream.mime)}`));
  box.appendChild(download);
  host.appendChild(box);
  host.dataset.video = 'unsupported';
}

/** Resolves true once the browser has read the stream's metadata, false when it cannot decode it. */
function whenLoaded(video: HTMLVideoElement): Promise<boolean> {
  return new Promise((resolve) => {
    video.addEventListener('loadedmetadata', () => resolve(true), { once: true });
    video.addEventListener('error', () => resolve(false), { once: true });
  });
}

/** Builds the player (or the fallback) in `host`; returns its cleanup. */
export async function renderVideo(shell: ViewerShell, series: SeriesRef, host: HTMLDivElement): Promise<() => void> {
  const stream = await fetchVideo(shell, series);
  const filename = series.seriesInstanceUID;
  if (!stream.playable || !document.createElement('video').canPlayType(stream.mime)) {
    showUnsupported(host, stream, filename);
    return () => host.replaceChildren();
  }
  const url = URL.createObjectURL(new Blob([stream.bytes], { type: stream.mime }));
  const video = document.createElement('video');
  video.className = 'display-video';
  video.controls = true;
  video.loop = true;
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';
  video.title = VIDEO_TEXT.playerTitle;
  const loaded = whenLoaded(video);
  video.src = url;
  host.appendChild(video);
  if (await loaded) {
    host.dataset.video = 'playing';
  } else {
    video.removeAttribute('src');
    video.load();
    showUnsupported(host, stream, filename);
  }
  return () => {
    video.pause();
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(url);
  };
}
